import { createApiClient } from '../../../shared/api/apiClient'
import { createAxiosClient } from '../../../shared/api/axiosClient'
import apiConfig from '../../../shared/config/apiConfig'
import { runWithConcurrency } from '../../../shared/utils/concurrency'
import {
  createChunkEndPayload,
  createChunkPayload,
  createChunkStartPayload,
  createSimpleUploadPayload,
  mapEnterpriseUploadResponse,
} from '../mappers/documentUploadMapper'

const appendUploadKey = (url, uploadKey) => {
  const uploadUrl = new URL(url)
  uploadUrl.searchParams.set('key', uploadKey)
  return uploadUrl.toString()
}

const multipartHeaders = { 'Content-Type': 'multipart/form-data' }

// The byte transfer is only the first 90% of a document's progress; the
// remainder covers the chunk/end call and the Brand DNA save that follows.
const TRANSFER_PROGRESS_CEILING = 90

// A resumed session has to describe the same partitioning as the current
// config, or its recorded etags no longer line up with the chunks we would
// send. A mismatch starts the multipart upload over rather than corrupting it.
const resumableSession = (session, { chunkSize, totalChunks }) => {
  if (!session?.assetId || !session?.uploadId) return null
  if (session.chunkSize !== chunkSize) return null
  if (
    !Array.isArray(session.partEtags) ||
    session.partEtags.length !== totalChunks
  ) {
    return null
  }
  return { ...session, partEtags: [...session.partEtags] }
}

export const createEnterpriseUploadService = ({
  client,
  config = apiConfig.enterpriseUploads,
} = {}) => {
  const {
    baseUrl = '',
    chunkConcurrency = 5,
    chunkSizeBytes = 5 * 1024 * 1024,
    simpleUploadMaxBytes = 5 * 1024 * 1024,
    tenantId = '',
    uploadKey = '',
  } = config || {}
  const apiClient =
    client ||
    createApiClient({
      axiosInstance: createAxiosClient({ timeout: 0 }),
    })

  const endpoint = (path) =>
    appendUploadKey(`${baseUrl.replace(/\/$/, '')}${path}`, uploadKey)

  const chunkByteLength = (file, chunkIndex) =>
    Math.min((chunkIndex + 1) * chunkSizeBytes, file.size) -
    chunkIndex * chunkSizeBytes

  const uploadSimpleFile = async (context, { onProgress }) => {
    const response = await apiClient.post(endpoint('/files'), {
      body: createSimpleUploadPayload({ ...context, tenantId }),
      headers: multipartHeaders,
      onUploadProgress: ({ loaded, total }) => {
        const uploadTotal = Math.max(total || context.file.size, 1)
        onProgress?.(
          Math.min(
            TRANSFER_PROGRESS_CEILING,
            Math.round((loaded / uploadTotal) * TRANSFER_PROGRESS_CEILING),
          ),
        )
      },
    })
    return mapEnterpriseUploadResponse(response)
  }

  const startChunkedUpload = async (file, totalChunks) => {
    const response = await apiClient.post(endpoint('/files/chunks/start'), {
      body: createChunkStartPayload({ file, tenantId }),
    })
    const {
      asset_id: assetId,
      job_id: jobId,
      upload_id: uploadId,
    } = response || {}
    if (!assetId || !uploadId) {
      throw new Error('Enterprise Uploads could not initialize this file.')
    }
    return {
      assetId,
      chunkSize: chunkSizeBytes,
      jobId,
      partEtags: new Array(totalChunks).fill(''),
      uploadId,
    }
  }

  // `onSession` publishes the multipart state — asset id, upload id, and the
  // etags confirmed so far — every time it advances. The caller keeps the last
  // one it saw, so a retry resends only the chunks that never landed.
  const uploadChunkedFile = async (context, { onProgress, onSession }) => {
    const { file } = context
    const totalChunks = Math.ceil(file.size / chunkSizeBytes)
    let session = resumableSession(context.uploadSession, {
      chunkSize: chunkSizeBytes,
      totalChunks,
    })

    if (!session) {
      session = await startChunkedUpload(file, totalChunks)
      onSession?.(session)
    }

    const loadedByChunk = session.partEtags.map((etag, chunkIndex) =>
      etag ? chunkByteLength(file, chunkIndex) : 0,
    )
    const reportProgress = () => {
      const loaded = loadedByChunk.reduce((total, value) => total + value, 0)
      onProgress?.(
        Math.min(
          TRANSFER_PROGRESS_CEILING,
          Math.round(
            (loaded / Math.max(file.size, 1)) * TRANSFER_PROGRESS_CEILING,
          ),
        ),
      )
    }
    reportProgress()

    const pendingChunks = session.partEtags.reduce(
      (indexes, etag, chunkIndex) =>
        etag ? indexes : [...indexes, chunkIndex],
      [],
    )

    await runWithConcurrency(
      pendingChunks,
      chunkConcurrency,
      async (chunkIndex) => {
        const response = await apiClient.post(endpoint('/files/chunks'), {
          body: createChunkPayload({
            assetId: session.assetId,
            chunkIndex,
            chunkSize: chunkSizeBytes,
            file,
            uploadId: session.uploadId,
          }),
          headers: multipartHeaders,
          // Multipart framing makes `loaded` run slightly past the chunk's own
          // size, so cap it before it inflates the file-wide total.
          onUploadProgress: ({ loaded }) => {
            loadedByChunk[chunkIndex] = Math.min(
              loaded,
              chunkByteLength(file, chunkIndex),
            )
            reportProgress()
          },
        })
        if (!response?.chunk_etag) {
          throw new Error(
            `Enterprise Uploads did not confirm chunk ${chunkIndex + 1}.`,
          )
        }
        session.partEtags[chunkIndex] = response.chunk_etag
        loadedByChunk[chunkIndex] = chunkByteLength(file, chunkIndex)
        onSession?.({ ...session, partEtags: [...session.partEtags] })
        reportProgress()
      },
    )

    const endResponse = await apiClient.post(endpoint('/files/chunks/end'), {
      body: createChunkEndPayload({
        assetId: session.assetId,
        brand: context.brand,
        file,
        jobId: session.jobId,
        partEtags: session.partEtags,
        tenantId,
        uploadId: session.uploadId,
      }),
    })
    return mapEnterpriseUploadResponse(endResponse)
  }

  return {
    uploadDocument: async (context, { onProgress, onSession } = {}) => {
      if (!baseUrl || !tenantId || !uploadKey) {
        throw new Error('Enterprise Uploads is not configured.')
      }
      if (!context.brand?.assetHubProjectUuid) {
        throw new Error('AssetHub project is not configured for this brand.')
      }

      return context.file.size < simpleUploadMaxBytes
        ? uploadSimpleFile(context, { onProgress })
        : uploadChunkedFile(context, { onProgress, onSession })
    },
  }
}

export const enterpriseUploadService = createEnterpriseUploadService()
